import React from "react";
import { graphql } from "gatsby";
import { classNames, markdownToHTML } from "utils";
import { useMediaQuery } from "react-responsive";

import HTMLContainer from "components/HTMLContainer";
import getBackground from "components/Backgrounds";
import getIllustration from "components/Illustrations";
import Image from "components/Image";
import { COLOR_GRADIENT, CUBE_POSITIONS } from "shared/constants";

import * as styles from "./SectionContent.module.less";

import FloatingWhiteCube from "./floating_white_cube.svg";
import TransparentBlueCube from "./transparent_blue_cube.svg";
import ButtonsContainer from "./ButtonsContainer";

/**
 * We use this to define what will be queried for this component
 * This also serves as typings (See `Gatsby.SectionContentDataFragment` below)
 */
export const SectionContentDataFragment = graphql`
  fragment CustomIllustration on MarkdownRemarkFrontmatterSectionsCustomIllustration {
    image {
      illustration: childImageSharp {
        # maxWidth: maximum size this could ever have. It will be fluid anyways
        fluid(maxWidth: 540, quality: 90) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
      extension
      publicURL
    }
    alt
    caption
  }

  fragment SectionContentData on MarkdownRemarkFrontmatterSections {
    background
    illustration
    customIllustration {
      ...CustomIllustration
    }
    preTitle
    title
    content
    ...SectionContentButtonsData
    isInverse
    isHero
    transitioningCubes {
      largeCubePosition
      hasSmallCube
    }
    section_id
  }
`;

/**
 * @param {GatsbyTypes.SectionContentDataFragment} props
 */
export default function SectionContent({
  preTitle,
  title,
  content,
  buttons,
  background,
  illustration,
  customIllustration,
  isInverse,
  isHero,
  transitioningCubes: { largeCubePosition, hasSmallCube },
  section_id,
}) {
  // Screen-Sm Ant default
  const isScreenSmall = useMediaQuery({
    query: "(max-width: 576px)",
  });
  const isScreenSmallerThanMedium = useMediaQuery({
    query: "(max-width: 768px)",
  });

  // @ts-expect-error
  const backgroundImage = getBackground(background);

  let illustrationNode = null;
  if (!isScreenSmallerThanMedium) {
    if (customIllustration?.image) {
      if (typeof customIllustration.image === "string") {
        illustrationNode = (
          <Image
            src={null}
            title={customIllustration.caption}
            alt={customIllustration.alt}
            publicURL={customIllustration.image}
            className={styles.customIllustration}
          />
        );
      } else {
        illustrationNode = (
          <Image
            src={customIllustration.image.illustration}
            title={customIllustration.caption}
            alt={customIllustration.alt}
            publicURL={customIllustration.image.publicURL}
            className={styles.customIllustration}
          />
        );
      }
    } else {
      // @ts-expect-error: graphql type is string
      illustrationNode = getIllustration(illustration, { isInverse });
    }
  }

  return (
    <section
      id={section_id}
      className={classNames(styles.sectionContent, {
        [styles.inverseSection]: Boolean(isInverse),
        [styles.hasCubes]: Boolean(largeCubePosition),
        [styles.isHero]: Boolean(isHero),
      })}
      style={
        !isScreenSmallerThanMedium
          ? backgroundImage && {
              backgroundImage: `url(${backgroundImage})${
                Boolean(isHero) ? `` : `, ${COLOR_GRADIENT}`
              }`,
            }
          : {}
      }
    >
      <div className={styles.container}>
        <div
          className={classNames(styles.content, {
            [styles.hasIllustration]: Boolean(illustrationNode),
          })}
        >
          {preTitle ? <p className={styles.preTitle}>{preTitle}</p> : null}
          <h2>{title}</h2>
          <HTMLContainer
            html={markdownToHTML(content)}
            className={styles.contentBody}
          />
          <ButtonsContainer buttons={buttons} />
        </div>

        {illustrationNode}

        {!isScreenSmall && largeCubePosition && (
          <div className={styles.cubes}>
            <img
              className={classNames(styles.floatingWhite, {
                [styles.rightOfContent]:
                  largeCubePosition === CUBE_POSITIONS.RIGHT_OF_CONTENT,
                [styles.leftOfContent]:
                  largeCubePosition === CUBE_POSITIONS.LEFT_OF_CONTENT,
                [styles.rightOfIllustration]:
                  largeCubePosition === CUBE_POSITIONS.RIGHT_OF_ILLUSTRATION,
              })}
              src={FloatingWhiteCube}
              alt="Floating White Cube"
            />
            {hasSmallCube && (
              <img
                className={classNames(styles.transparentBlue, {
                  [styles.rightOfContent]:
                    largeCubePosition === CUBE_POSITIONS.RIGHT_OF_CONTENT,
                  [styles.leftOfContent]:
                    largeCubePosition === CUBE_POSITIONS.LEFT_OF_CONTENT,
                  [styles.rightOfIllustration]:
                    largeCubePosition === CUBE_POSITIONS.RIGHT_OF_ILLUSTRATION,
                })}
                src={TransparentBlueCube}
                alt="Transparent Blue Cube"
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
